import {
  Box,
  Step,
  StepLabel,
  Stepper,
  StepConnector,
  styled,
  useTheme,
} from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { AdRoutes } from "../constants/ad-routes";
import { CheckOutlined } from "@mui/icons-material";

const REGION_WORKFLOW_STEPS = ["Create Sequence", "Create Region"];

// Custom Connector with specified distance
const CustomConnector = styled(StepConnector)(({ theme }) => ({
  "&.MuiStepConnector-root": {
    top: "50%",
    left: "calc(-50% + 16px)", // Reduced spacing for ~27px total
    right: "calc(50% - 16px)", // Reduced spacing for ~27px total
    maxWidth: "27px",
  },
  "& .MuiStepConnector-line": {
    borderColor: theme.palette.grey[200], // #C1C7CE
    borderTopWidth: 1,
    borderRadius: 1,
  },
}));

// Custom Step Icon
const CustomStepIcon = styled("div")<{
  stepIndex: number;
  activeStep: number;
}>(({ theme, stepIndex, activeStep }) => {
  const isCompleted = stepIndex < activeStep;
  const isActive = stepIndex === activeStep;

  return {
    width: 24,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    backgroundColor: isCompleted ? "#005140" : theme.palette.grey[600], // #282A2D for both active and inactive
    color: isCompleted
      ? "#7EF8D4"
      : isActive
        ? "#FFFFFF"
        : theme.palette.grey[200], // #C1C7CE
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "bold",
  };
});

interface RegionWorkflowStepperProps {
  className?: string;
}

const RegionWorkflowStepper: React.FC<RegionWorkflowStepperProps> = ({
  className,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { regionId } = useParams<{ regionId: string }>();
  const location = useLocation();

  // Determine current step based on current route
  const isSequencePage =
    location.pathname.includes("create-sequence") ||
    location.pathname.includes("re-sequence") ||
    location.pathname.includes("add-unassigned-to-region");
  const activeStep = isSequencePage ? 0 : 1;

  const handleStepClick = (stepIndex: number) => {
    if (stepIndex === 0 && !isSequencePage) {
      // Navigate to create sequence page
      if (regionId) {
        // If editing existing region, go to resequence route
        navigate(AdRoutes.resequenceRoute.replace(":regionId", regionId));
      } else {
        // If creating new region, go to create sequence
        navigate(AdRoutes.createSequence);
      }
    } else if (stepIndex === 1 && isSequencePage) {
      // Navigate to create region page
      if (regionId) {
        // If editing existing region, go to edit region details
        navigate(AdRoutes.editRegionDetails.replace(":regionId", regionId));
      } else {
        // If creating new region, go to create region details
        navigate(AdRoutes.createRegionDetails);
      }
    }
  };

  return (
    <Box className={className} sx={{ maxWidth: "300px" }}>
      <Stepper
        activeStep={activeStep}
        connector={<CustomConnector />}
        style={{ marginTop: "15px", marginBottom: "20px" }}
      >
        {REGION_WORKFLOW_STEPS.map((label, index) => {
          const isCompleted = index < activeStep;
          const isActive = index === activeStep;
          const stepProps: { completed?: boolean } = {};
          const labelProps: {
            optional?: React.ReactNode;
          } = {};

          return (
            <Step key={label} {...stepProps}>
              <StepLabel
                {...labelProps}
                sx={{
                  cursor: "pointer",
                  "& .MuiStepLabel-label": {
                    color: isActive ? "#FFFFFF" : theme.palette.grey[200], // #C1C7CE for inactive
                    fontWeight: 500,
                  },
                }}
                onClick={() => handleStepClick(index)}
                StepIconComponent={() => (
                  <CustomStepIcon
                    stepIndex={index}
                    activeStep={activeStep}
                    onClick={() => handleStepClick(index)}
                  >
                    {isCompleted ? (
                      <CheckOutlined sx={{ width: "14px", height: "14px" }} />
                    ) : (
                      index + 1
                    )}
                  </CustomStepIcon>
                )}
              >
                {label}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

export default RegionWorkflowStepper;
